import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  scrolled: boolean;
}

const LANGUAGES = [
  { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English' },
  { code: 'de', label: 'DE', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'es', label: 'ES', flag: '🇨🇱', name: 'Español' },
];

export default function Navbar({ scrolled }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const openAssistant = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) widget.click();
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md transition-all duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 md:py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/fdb1d692218b16774433254afb67402e/62228a1116343f550020e817b40b73a2.jpeg"
              alt="GaLi Travel Chile"
              className="h-14 md:h-20 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {[
              { key: 'nav_home', id: 'inicio' },
              { key: 'nav_destinations', id: 'destinos' },
              { key: 'nav_gallery', id: 'galeria' },
              { key: 'nav_offers', id: 'tours' },
              { key: 'nav_testimonials', id: 'testimonios' },
              { key: 'nav_contact', id: 'contacto' },
            ].map(({ key, id }) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t(key)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((prev) => !prev)}
                className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border border-gray-200 text-gray-700 hover:border-teal-400 hover:text-teal-600"
              >
                <span>{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>

              {langOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-teal-50 hover:text-teal-700 ${
                        i18n.language === lang.code ? 'bg-teal-50 text-teal-700' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Assistant Button - Desktop */}
            <button
              onClick={openAssistant}
              className="hidden md:flex relative items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line text-lg"></i>
              <span className="text-sm font-medium">{t('nav_assistant')}</span>
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            </button>

            {/* Hamburger Button - Mobile */}
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="md:hidden flex items-center justify-center w-10 h-10 text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100">
            <div className="flex flex-col space-y-3 pt-4">
              {[
                { key: 'nav_home', id: 'inicio' },
                { key: 'nav_destinations', id: 'destinos' },
                { key: 'nav_gallery', id: 'galeria' },
                { key: 'nav_offers', id: 'tours' },
                { key: 'nav_testimonials', id: 'testimonios' },
                { key: 'nav_contact', id: 'contacto' },
              ].map(({ key, id }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="text-left text-base font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-2 px-2"
                >
                  {t(key)}
                </button>
              ))}
              
              {/* Assistant Button - Mobile */}
              <button
                onClick={() => {
                  openAssistant();
                  setMenuOpen(false);
                }}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap mt-2"
              >
                <i className="ri-customer-service-2-line text-lg"></i>
                <span className="text-sm font-medium">{t('nav_assistant')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}